/**
 * TabsContext — multi-tab navigation INSIDE the app/PWA window.
 *
 * Default state: a single "Main" tab whose URL follows the user's normal
 * AppBar navigation (Dashboard / Trades / Charts / etc.). Clicking a
 * "chart icon" anywhere in the app calls `openChartTab(symbol, exchange)`
 * which pushes a new chart tab and switches to it. Chart tabs persist
 * across reloads (localStorage).
 *
 * Why this exists: PWAs installed via Chrome/Edge cannot spawn additional
 * PWA windows or natively-tabbed sub-windows via `window.open`. The
 * `window.open(url, '_blank')` call there leaks the new view out into the
 * system browser, which the user explicitly didn't want. This module gives
 * us "tabs" inside the single PWA window. Browser users get the same UX
 * (in-app tabs instead of multiple browser tabs) for consistency.
 *
 * State machine:
 *   - tabs:        always has Main as tabs[0]; chart tabs appended in open-order
 *   - activeTabId: derived from the URL on every render — if the URL matches a
 *                  chart tab's symbol, that tab is active; otherwise Main is
 *                  active (which means the user is on Dashboard/Trades/etc.)
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export interface Tab {
  /** Stable id. `main` for the home tab; `chart:<SYM>:<EXCH>` for chart tabs. */
  id: string;
  kind: 'main' | 'chart';
  symbol?: string;
  exchange?: string;
  /** Display label (Main tab uses route-derived label). */
  label: string;
  /** URL to navigate to when this tab is selected. Updated for Main as user navs. */
  url: string;
}

const MAIN_ID = 'main';
const MAIN_DEFAULT: Tab = { id: MAIN_ID, kind: 'main', label: 'Main', url: '/dashboard' };

interface Ctx {
  tabs: Tab[];
  activeTabId: string;
  /** Open a chart tab for this symbol (dedup by symbol+exchange); switch to it. */
  openChartTab: (symbol: string, exchange?: string, extraQuery?: Record<string, string>) => void;
  /** Rename the ACTIVE chart tab to a new symbol in place (label + url + id) and
   *  navigate to it. Used when the user changes symbol from inside a chart tab so
   *  the tab strip + browser/PWA title track the displayed symbol. No-op when the
   *  active tab isn't a chart tab. */
  syncActiveChartTab: (symbol: string, exchange?: string) => void;
  /** Close a chart tab (Main tab is uncloseable). If closing the active tab,
   *  switches to the previous tab. */
  closeTab: (id: string) => void;
  /** Switch to an existing tab (navigates to its url). */
  switchTab: (id: string) => void;
}

const TabsContext = createContext<Ctx | null>(null);

const STORAGE_KEY = 'pwa.tabs.v1';

const loadTabs = (): Tab[] => {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    if (Array.isArray(raw) && raw[0]?.id === MAIN_ID) return raw;
  } catch { /* ignore */ }
  return [MAIN_DEFAULT];
};

const persistTabs = (tabs: Tab[]) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(tabs)); } catch { /* ignore */ }
};

/** Friendly label for a non-chart route (used to label the Main tab dynamically). */
const labelForPath = (path: string): string => {
  if (path.startsWith('/dashboard'))   return 'Dashboard';
  if (path.startsWith('/trades'))      return 'Trades';
  if (path.startsWith('/analytics'))   return 'Analytics';
  if (path.startsWith('/alerts'))      return 'Alerts';
  if (path.startsWith('/instruments')) return 'Instruments';
  if (path.startsWith('/logs'))        return 'Logs';
  if (path.startsWith('/option-strategies')) return 'Options AT';
  if (path.startsWith('/options'))     return 'F&O';
  // /edge-ai now redirects to /ai-desk?tab=engine. Both map to the same title so a tab that
  // was opened on the old path does not keep a name the app no longer has.
  if (path.startsWith('/edge-ai'))     return 'AI Desk';
  if (path.startsWith('/ai-desk'))     return 'AI Desk';
  if (path.startsWith('/strategies'))  return 'Strategies';
  if (path.startsWith('/charts'))      return 'Charts';
  if (path.startsWith('/backtest'))    return 'Backtest';
  if (path.startsWith('/settings'))    return 'Settings';
  if (path.startsWith('/app-guide'))     return 'App Guide';
  if (path.startsWith('/help'))          return 'App Guide';
  if (path.startsWith('/trading-guide')) return 'App Guide';
  return 'Main';
};

/** Parse a `/charts/<sym>` URL into symbol + exchange (from query). Null otherwise. */
const parseChartUrl = (path: string, search: string): { symbol: string; exchange: string } | null => {
  const m = path.match(/^\/charts\/([^/]+)/);
  if (!m) return null;
  const symbol = decodeURIComponent(m[1]).toUpperCase();
  const sp = new URLSearchParams(search);
  const exchange = (sp.get('exchange') || '').toUpperCase();
  return { symbol, exchange };
};

const chartTabId = (symbol: string, exchange: string) => `chart:${symbol.toUpperCase()}:${(exchange || '').toUpperCase()}`;

export const TabsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tabs, setTabs] = useState<Tab[]>(loadTabs);

  // Derive active tab from the URL (so back/forward buttons stay in sync).
  const activeTabId = useMemo(() => {
    const parsed = parseChartUrl(location.pathname, location.search);
    if (parsed) {
      const id = chartTabId(parsed.symbol, parsed.exchange);
      const match = tabs.find(t => t.id === id);
      if (match) return id;
      // Chart URL but no tab — fall through to Main; the tab list will be
      // updated by openChartTab() in the next effect below.
    }
    return MAIN_ID;
  }, [location.pathname, location.search, tabs]);

  // Keep the Main tab's URL + label tracking the user's free-form navigation
  // whenever they're NOT on a chart-tab route. So switching back to Main
  // returns them to wherever they last were (Dashboard / Trades / etc.).
  //
  // F-Tabs-ChartLeak — when openChartTab fires, navigate('/charts/...') and
  // setTabs(add chart tab) race. For one render the URL is /charts/SYM but
  // tabs[] doesn't include it yet, so activeTabId falls through to MAIN_ID.
  // Before the fix this useEffect overwrote Main's URL to /charts/SYM and
  // permanently leaked the chart route into Main. Skip the update for any
  // /charts/* path so Main always points at the last NON-chart route.
  useEffect(() => {
    if (activeTabId !== MAIN_ID) return;
    if (location.pathname.startsWith('/charts/')) return;
    const fullPath = location.pathname + location.search;
    const label = labelForPath(location.pathname);
    setTabs(prev => {
      const main = prev[0];
      if (main.url === fullPath && main.label === label) return prev;
      return [{ ...main, url: fullPath, label }, ...prev.slice(1)];
    });
  }, [activeTabId, location.pathname, location.search]);

  // If the user navigated directly to a `/charts/<sym>` URL that isn't yet
  // in the tab list (browser back/forward, deep link, etc.), auto-create a
  // tab for it so the strip stays in sync.
  useEffect(() => {
    const parsed = parseChartUrl(location.pathname, location.search);
    if (!parsed) return;
    const id = chartTabId(parsed.symbol, parsed.exchange);
    setTabs(prev => {
      if (prev.find(t => t.id === id)) return prev;
      const url = location.pathname + location.search;
      return [...prev, { id, kind: 'chart', symbol: parsed.symbol, exchange: parsed.exchange, label: parsed.symbol, url }];
    });
  }, [location.pathname, location.search]);

  // Persist
  useEffect(() => { persistTabs(tabs); }, [tabs]);

  const openChartTab = useCallback((symbol: string, exchange?: string, extraQuery?: Record<string, string>) => {
    const sym = String(symbol || '').toUpperCase();
    if (!sym) return;
    const exch = String(exchange || '').toUpperCase();
    const id = chartTabId(sym, exch);
    const params = new URLSearchParams();
    if (exch) params.set('exchange', exch);
    if (!extraQuery || !('preset' in extraQuery)) params.set('preset', 'cloud');
    for (const [k, v] of Object.entries(extraQuery || {})) params.set(k, v);
    const url = `/charts/${encodeURIComponent(sym)}?${params.toString()}`;

    setTabs(prev => {
      if (prev.find(t => t.id === id)) return prev;
      return [...prev, { id, kind: 'chart', symbol: sym, exchange: exch, label: sym, url }];
    });
    navigate(url);
  }, [navigate]);

  const syncActiveChartTab = useCallback((symbol: string, exchange?: string) => {
    const sym = String(symbol || '').toUpperCase();
    if (!sym) return;
    // Only rename when the user is actually inside a chart tab (leave Main alone).
    if (!activeTabId.startsWith('chart:')) return;
    const exch = String(exchange || '').toUpperCase();
    const newId = chartTabId(sym, exch);
    if (newId === activeTabId) return; // already showing this symbol
    const params = new URLSearchParams();
    if (exch) params.set('exchange', exch);
    params.set('preset', 'cloud');
    const url = `/charts/${encodeURIComponent(sym)}?${params.toString()}`;
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === activeTabId);
      if (idx < 0) return prev;
      const renamed: Tab = { id: newId, kind: 'chart', symbol: sym, exchange: exch, label: sym, url };
      // Rename the active tab in place; drop any other tab already pointing at
      // the new symbol so we don't end up with duplicates.
      return prev
        .map((t, i) => (i === idx ? renamed : t))
        .filter((t, i) => i === idx || t.id !== newId);
    });
    navigate(url, { replace: true });
  }, [activeTabId, navigate]);

  const switchTab = useCallback((id: string) => {
    const t = tabs.find(x => x.id === id);
    if (!t) return;
    navigate(t.url);
  }, [tabs, navigate]);

  const closeTab = useCallback((id: string) => {
    if (id === MAIN_ID) return;
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === id);
      if (idx < 0) return prev;
      const next = prev.filter(t => t.id !== id);
      // If we closed the active tab, navigate to a sensible neighbour.
      if (id === activeTabId) {
        const fallback = next[idx - 1] || next[idx] || next[0] || MAIN_DEFAULT;
        // Schedule navigation outside the setter to avoid React warning
        setTimeout(() => navigate(fallback.url), 0);
      }
      return next;
    });
  }, [activeTabId, navigate]);

  const value = useMemo<Ctx>(() => ({ tabs, activeTabId, openChartTab, syncActiveChartTab, closeTab, switchTab }),
    [tabs, activeTabId, openChartTab, syncActiveChartTab, closeTab, switchTab]);

  return <TabsContext.Provider value={value}>{children}</TabsContext.Provider>;
};

/**
 * Hook to access tabs. Outside the provider it falls back to the legacy
 * `window.open('/charts/...', '_blank')` behaviour so call-sites are safe
 * to use everywhere — they just degrade gracefully if no provider is mounted.
 */
export function useTabs(): Ctx {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    return {
      tabs: [MAIN_DEFAULT],
      activeTabId: MAIN_ID,
      openChartTab: (symbol, exchange, extra) => {
        const sym = String(symbol || '').toUpperCase();
        if (!sym) return;
        const params = new URLSearchParams();
        if (exchange) params.set('exchange', String(exchange).toUpperCase());
        if (!extra || !('preset' in extra)) params.set('preset', 'cloud');
        for (const [k, v] of Object.entries(extra || {})) params.set(k, v);
        window.open(`/charts/${encodeURIComponent(sym)}?${params.toString()}`, '_blank');
      },
      syncActiveChartTab: () => { /* no-op without provider */ },
      closeTab: () => { /* no-op */ },
      switchTab: () => { /* no-op */ },
    };
  }
  return ctx;
}
